/**
 * useResizablePanels Hook
 * 
 * Provides resizable panel functionality with drag handles
 */

import { useState, useCallback, useEffect, useRef } from 'react'

interface UseResizablePanelsOptions {
  defaultLeftWidth?: number
  minLeftWidth?: number
  maxLeftWidth?: number
  storageKey?: string
}

export interface UseResizablePanelsResult {
  leftWidth: number
  rightWidth: number
  handleMouseDown: (e: React.MouseEvent) => void
  isDragging: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
}

export function useResizablePanels(options: UseResizablePanelsOptions = {}): UseResizablePanelsResult {
  const {
    defaultLeftWidth = 33.33,
    minLeftWidth = 3,
    maxLeftWidth = 97,
    storageKey = 'resizablePanel_leftWidth'
  } = options

  // Load saved width from localStorage
  const getSavedWidth = (): number => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const width = parseFloat(saved)
        if (width >= minLeftWidth && width <= maxLeftWidth) {
          return width
        }
      }
    }
    return defaultLeftWidth
  }

  const [leftWidth, setLeftWidth] = useState<number>(getSavedWidth())
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return

    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()
    const offsetX = e.clientX - containerRect.left
    const newLeftWidth = (offsetX / containerRect.width) * 100

    // Constrain to min/max
    if (newLeftWidth >= minLeftWidth && newLeftWidth <= maxLeftWidth) {
      setLeftWidth(newLeftWidth)
      
      // Save to localStorage
      if (storageKey) {
        localStorage.setItem(storageKey, newLeftWidth.toString())
      }
    }
  }, [isDragging, minLeftWidth, maxLeftWidth, storageKey])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    } else {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  return {
    leftWidth,
    rightWidth: 100 - leftWidth,
    handleMouseDown,
    isDragging,
    containerRef
  }
}

